import type BaseEnumType from "@/app/services/baseEnumType";
import type { Order } from "@/app/services/generated/orderService";
import type { Visit } from "@/app/services/generated/visitService";
import Key from "@/app/types/common/key";
import { translate } from "@/plugins/i18n";

export enum JourneyPanelTypeEnum {
  allOrders = 0,
  collection = 1,
  courier = 2,
}

export interface JourneyPanelTab {
  journeyPanelType: JourneyPanelTypeEnum;
  label: string;
  numberOfOrders?: number;
}

export interface OrderVisits {
  orders: Order[];
  visits: Visit[];
}

export class EnumDistributionItemAction implements BaseEnumType {
  private static allValues: EnumDistributionItemAction[] = []; // make sure this is top-most

  public static completeOrder = new EnumDistributionItemAction(
    1,
    "CompleteOrder",
    "enum_distribution_item_actions_mark_as_complete"
  );
  public static cancelOrder = new EnumDistributionItemAction(
    2,
    "CancelOrder",
    "enum_distribution_item_actions_cancel_order"
  );
  public static uncompleteOrder = new EnumDistributionItemAction(
    3,
    "UncompleteOrder",
    "enum_distribution_item_actions_uncomplete_order"
  );
  public static rescheduleOrder = new EnumDistributionItemAction(
    4,
    "RescheduleOrder",
    "enum_distribution_item_actions_reschedule_order"
  );
  public static completeVisit = new EnumDistributionItemAction(
    5,
    "CompleteVisit",
    "enum_distribution_item_actions_complete_visit"
  );
  public static cancelVisit = new EnumDistributionItemAction(
    6,
    "CancelVisit",
    "enum_distribution_item_actions_cancel_visit"
  );
  public static uncompleteVisit = new EnumDistributionItemAction(
    7,
    "UncompleteVisit",
    "enum_distribution_item_actions_uncomplete_visit"
  );
  public static rescheduleVisit = new EnumDistributionItemAction(
    8,
    "RescheduleVisit",
    "enum_distribution_item_actions_reschedule_visit"
  );
  public static viewDelivery = new EnumDistributionItemAction(
    9,
    "ViewDelivery",
    "enum_distribution_item_actions_view_delivery",
    true
  );
  public static viewVisit = new EnumDistributionItemAction(
    10,
    "ViewVisit",
    "enum_distribution_item_actions_view_visit",
    true
  );
  public static viewOrder = new EnumDistributionItemAction(
    11,
    "ViewOrder",
    "enum_distribution_item_actions_view_order",
    true
  );
  public static changeStatus = new EnumDistributionItemAction(
    12,
    "ChangeStatus",
    "enum_distribution_item_actions_change_status"
  );
  public static clearVisit = new EnumDistributionItemAction(
    13,
    "ClearVisit",
    "enum_distribution_item_actions_clear_visit"
  );
  public static resolveAndCompleteOrder = new EnumDistributionItemAction(
    14,
    "ResolveAndCompleteOrder",
    "enum_distribution_item_actions_resolve_and_mark_as_complete"
  );
  public static ignoreAndCompleteOrder = new EnumDistributionItemAction(
    15,
    "ResolveAndCompleteOrder",
    "enum_distribution_item_actions_ignore_and_mark_as_complete"
  );
  public static editVisit = new EnumDistributionItemAction(
    16,
    "EditVisit",
    "enum_distribution_item_actions_edit_visit",
    false
  );

  public key: number;
  public value: string;
  public translationKey: string;
  public isRoutingAction?: boolean;

  private constructor(
    key: number,
    value: string,
    translationKey: string,
    isRoutingAction?: boolean
  ) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    this.isRoutingAction = isRoutingAction;
    EnumDistributionItemAction.allValues.push(this);
  }

  public get translation() {
    return translate(Key.fromString(this.translationKey));
  }

  public static getValues(): EnumDistributionItemAction[] {
    return EnumDistributionItemAction.allValues;
  }

  public static getByKey(key: number): EnumDistributionItemAction {
    const found = EnumDistributionItemAction.allValues.find(
      (e) => e.key === key
    );
    if (found === undefined) {
      throw new Error(`EnumDistributionItemAction - Key not found: ${key}`);
    }

    return found;
  }
}

export class EnumDistributionItemType implements BaseEnumType {
  private static allValues: EnumDistributionItemType[] = []; // make sure this is top-most

  public static order = new EnumDistributionItemType(
    1,
    "Order",
    "enum_distribution_item_type_order"
  );
  public static visit = new EnumDistributionItemType(
    1,
    "Visit",
    "enum_distribution_item_type_visit"
  );

  public key: number;
  public value: string;
  public translationKey?: string;

  private constructor(key: number, value: string, translationKey?: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumDistributionItemType.allValues.push(this);
  }

  public static getValues(): EnumDistributionItemType[] {
    return EnumDistributionItemType.allValues;
  }

  public static getByKey(key: number): EnumDistributionItemType {
    const found = EnumDistributionItemType.allValues.find((e) => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumDistributionItemType - Key not found: ${key}`);
    }

    return found;
  }
}
